import type { LocationQueryRaw, RouteLocationRaw } from "vue-router";

import { useReleaseStore } from "@/stores/release-store";

/**
 * Append the active draft revision (`?draft=<id>`) to an entity-navigation
 * target while a thread branch is being viewed.
 *
 * This makes the rendered href honest — hovering a link shows the real
 * destination, including the branch you'll stay on — and keeps you on the
 * branch when you click. It replaces the old `router.beforeEach` guard that
 * re-stamped the param *after* navigation (invisible to the user).
 *
 * Apply it only to design-mode, same-stek entity links/pushes. When no thread
 * branch is active it returns the target unchanged, so it's a no-op in the
 * working-copy / release views.
 *
 * `threadIdOverride` names the draft the LINK belongs to, for callers that
 * render a thread's content without standing on its branch — the thread detail
 * page and the thread drawer, where `activeThreadId` is null. Without it those
 * links render bare and land the user on /not-found, since the entity only
 * exists on the branch until the thread is accepted.
 */
export const withThreadRevision = (
    to: RouteLocationRaw,
    threadIdOverride?: string
): RouteLocationRaw => {
    // Reactive read: in a template binding the link re-resolves when the user
    // enters/leaves a thread branch. Skipped when the caller named the draft,
    // which is authoritative and does not change while the link is mounted.
    const threadId = threadIdOverride ?? useReleaseStore().activeThreadId;
    if (!threadId) return to;

    if (typeof to === "string") {
        // Entity link strings don't carry their own query, so attach it here.
        return { path: to, query: { draft: threadId } };
    }

    const existingQuery: LocationQueryRaw =
        (to as { query?: LocationQueryRaw }).query ?? {};
    const { thread: _legacyThread, ...query } = existingQuery;
    return { ...to, query: { ...query, draft: threadId } };
};
